<template>
  <process-layout title="ご注文完了画面" step="finish">
    <h3 class="process-title pt-5 text-center">ご注文ありがとうございました。</h3>

    <div class="order-number d-flex mt-10">
      <h3 class="process-title mb-0">
        ご注文番号 : <span class="text-danger">{{ props.orderNumber }}</span>
      </h3>
    </div>

    <div class="py-3 base-errors mb-10 mt-5" v-if="errorsHeader.length">
      <div v-for="(error, index) in errorsHeader" :key="index">
        {{ error }}
      </div>
    </div>

    <div class="thanks-page">
      <div>
        <p>ご注文いただきまして、誠にありがとうございます。</p>
        <p>ご登録いただきましたメールアドレス宛に、後ほど注文メールをお送りいたします。</p>
        <p>お手数ですが、ご注文内容にお間違いがないか必ずご確認ください。</p>
      </div>
      <div>
        <p>なお、注文処理状況によっては、メールの送信が遅れる場合がございます。</p>
        <p>
          また、万が一、メールが届かない場合は、迷惑メールフォルダに入っている可能性がありますので、そちらもご確認ください。
        </p>
      </div>
      <div>
        <p>
          何かご不明点やご質問等ございましたら、以下のメールアドレスまでお気軽にお問い合わせくださいませ。
        </p>
        <p>Mail: info@united-cart.jp</p>
      </div>
    </div>

    <notice v-if="lineError" variant="danger" class="mt-5">{{ lineError }}</notice>

    <line-login-button
      v-if="cartStore.urlInfo && cartStore.urlInfo.lineLoginThanksDisplay"
      origin="lp_thanks"
      :url-id="cartStore.urlInfo.id"
      :order-number="String(props.orderNumber)"
    />

    <div class="text-center">
      <a class="mx-2 back-btn p-0 border-0" :href="`/lp?u=${baseUrl}`"> ＜ トップページへ </a>
    </div>
  </process-layout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onMounted } from "vue";

import { useGlobalStore } from "@/stores/global";
import { useCartStore } from "@/stores/cart";

import ProcessLayout from "@/components/organisms/pages/lp/ProcessLayout.vue";
import LineLoginButton from "@/components/molecules/line/LineLoginButton.vue";

interface Props {
  orderNumber: String;
  baseUrl: String;
}

const props = defineProps<Props>();

const globalStore = useGlobalStore();
const cartStore = useCartStore();

const errorsHeader = computed(() => globalStore.validationBaseErrors ?? []);

// resolve_lp_thanks redirects every failure back here with a ready-made
// Japanese message. Without a surface the customer retries forever.
const lineError = computed(() => new URLSearchParams(window.location.search).get("line_error"));

onBeforeMount(() => {
  cartStore.lastPage = "finish";
});

// The purchase form and the confirm page both call cartStore.$reset()
// immediately before navigating here, which restores urlInfo to its initial
// literal (id: 0, lineLoginThanksDisplay: false). Persisted state therefore
// cannot be trusted on this page - without re-fetching, the LINE button can
// never render and url_id would be sent as 0.
onMounted(async () => {
  if (cartStore.urlInfo?.id) return;

  // Best-effort: the LINE button is the only thing that needs this, so a
  // failure must not surface on the order-complete page. The toast and the
  // loading overlay are raised INSIDE useGraphql, before the rejection, so
  // they have to be turned off at the call - the .catch below only stops the
  // unhandled rejection. Before this page fetched anything, a transient blip
  // here would have put a red toast in front of a customer who has just paid.
  await cartStore
    .fetchUrl(String(props.baseUrl), { errorToasted: false, loading: false })
    .catch(() => undefined);
});
</script>

<style lang="scss" scoped>
.order-number {
  .process-title {
    padding: 15px 38px;
    width: auto;
    border: 2px solid #000a4c;
    display: block;
    height: auto;
    margin: auto;
  }
}

.thanks-page {
  margin-top: 35px;
  h4,
  div {
    margin-bottom: 24px;
    p {
      font-weight: 700;
      font-size: 11px;
      line-height: 21px;
      text-align: center;
      color: #000000;
      margin-bottom: 0;
    }
  }
}
.back-btn {
  background-color: white !important;
  font-weight: 700;
  font-size: 11px;

  &:hover {
    background-color: white;
  }
}
@media screen and (max-width: 700px) {
  .order-number {
    .process-title {
      padding: 15px 0px;
      text-align: center;
      width: 100%;
      border: 2px solid #000a4c;
      display: block;
      height: auto;
      margin: auto;
    }
  }
}
</style>
